import React from 'react';
import GeneratedAtLabel from 'src/app/admin/components/stats-panel/GeneratedAtLabel';

export type BlastRadiusFigure = {
    label: string;
    value: number;
    /** Qualifies the count where the number alone would mislead, e.g. how users split. */
    note?: string;
};

type BlastRadiusListProps = {
    figures: BlastRadiusFigure[];
    generatedAt?: string;
    isLoading?: boolean;
    isError?: boolean;
};

/**
 * What an irreversible act would take with it, as a quiet ledger rather than a dashboard. The
 * figures matter, but in a confirmation the thing being deleted is what the reader must recognise
 * first — so these stay small and let the name above them carry the weight.
 */
const BlastRadiusList = ({ figures, generatedAt, isLoading = false, isError = false }: BlastRadiusListProps) => {
    if (isError) {
        return (
            <div className='rounded-lg border border-grey-20 bg-grey-0 px-4 py-3' data-testid='blast-radius'>
                <p className='text-sm text-grey-70'>These figures could not be loaded, so what this would remove is unknown.</p>
            </div>
        );
    }

    return (
        <div className='rounded-lg border border-grey-20 bg-grey-0 px-4 py-3' data-testid='blast-radius'>
            <dl className='flex flex-col divide-y divide-grey-20'>
                {figures.map(figure => (
                    <div key={figure.label} className='flex items-baseline justify-between gap-4 py-2' data-testid='blast-radius-row'>
                        <dt className='text-sm text-grey-70'>
                            {figure.label}
                            {figure.note && <span className='block text-xs text-grey-70'>{figure.note}</span>}
                        </dt>
                        <dd className='text-base font-medium text-black tabular-nums'>
                            {isLoading ? (
                                <span className='block h-4 w-12 animate-pulse rounded bg-grey-20' data-testid='blast-radius-skeleton' />
                            ) : (
                                figure.value.toLocaleString('en-GB')
                            )}
                        </dd>
                    </div>
                ))}
            </dl>

            {!isLoading && generatedAt && (
                <div className='mt-1 border-t border-grey-20 pt-2'>
                    <GeneratedAtLabel generatedAt={generatedAt} className='text-xs text-grey-70' />
                </div>
            )}
        </div>
    );
};

export default BlastRadiusList;
